import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { authService } from '@/services/authService';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { HeaderUserMenu } from '@/components/layout/HeaderUserMenu';
import { toast } from 'react-hot-toast';
import { NotificationBell } from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/ThemeToggle';

interface HeaderProps {
  onMenuClick: () => void;
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick, onToggleSidebar, sidebarCollapsed }) => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const [clienteSearch, setClienteSearch] = useState('');

  const handleClienteSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const term = clienteSearch.trim();
    if (term) {
      navigate(`/clientes-fornecedores?search=${encodeURIComponent(term)}`);
      setClienteSearch('');
    } else {
      navigate('/clientes-fornecedores');
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      toast.success('Logout realizado com sucesso!');
    } catch (error) {
      // Even if logout fails on server, clear local state
      logout();
    }
  };

  return (
    <header
      className="sticky top-0 z-40 flex h-16 w-full shrink-0 items-center border-b border-border bg-card px-4 shadow-soft-sm sm:px-6 lg:px-8 transition-shadow duration-200"
      data-topbar
    >
      {/* Esquerda: menu + toggle sidebar */}
      <div className="flex shrink-0 items-center gap-x-1 sm:gap-x-2">
        <button
          type="button"
          className="-m-2 p-2 rounded-input text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 lg:hidden"
          onClick={onMenuClick}
        >
          <span className="sr-only">Abrir sidebar</span>
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="hidden lg:flex -m-2 p-2 rounded-input text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? "Expandir sidebar" : "Recolher sidebar"}
        >
          <span className="sr-only">{sidebarCollapsed ? "Expandir sidebar" : "Recolher sidebar"}</span>
          {sidebarCollapsed ? (
            <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
        <div className="h-6 w-px bg-border lg:hidden" aria-hidden="true" />
      </div>

      {/* Campo de busca: ocupa todo o espaço até os ícones da direita */}
      <div className="flex min-w-0 flex-1 items-center gap-2 pl-2 sm:pl-4">
        <form onSubmit={handleClienteSearch} className="flex min-w-0 flex-1 items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" aria-hidden="true" />
            <Input
              type="search"
              placeholder="Buscar cliente (nome, placa ou CPF)..."
              value={clienteSearch}
              onChange={(e) => setClienteSearch(e.target.value)}
              className="h-9 w-full min-w-0 pl-9 bg-muted/50 border-border"
              aria-label="Buscar cliente por nome, placa ou CPF"
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Buscar cliente"
          >
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {/* Direita: tema, notificações, perfil, logout (colados à direita) */}
      <div className="flex shrink-0 items-center gap-x-3 sm:gap-x-4 lg:gap-x-6">
        <ThemeToggle />
        <NotificationBell />
        <div className="hidden lg:block h-6 w-px bg-border" aria-hidden="true" />
        <div className="flex items-center gap-x-2 lg:gap-x-3">
          <HeaderUserMenu />
          <Button variant="ghost" size="icon" onClick={handleLogout} title="Sair" className="rounded-input">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};
